import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowCounterClockwise, CheckCircle, Plus, X, House, Sparkle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { roomImage } from "@/lib/roomImages";

/** Standard Deadline-Vorschlag: heute 14:00, oder wenn schon vorbei → morgen 11:00 */
function defaultDeadline() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 0);
  if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1) || d.setHours(11, 0, 0, 0);
  // datetime-local braucht "YYYY-MM-DDTHH:MM" (lokal)
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const fmtDeadline = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString("de-DE", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" });
};
const isOverdue = (v) => v && !isNaN(new Date(v)) && new Date(v).getTime() < Date.now();

/**
 * Reinigungs-Checkliste je Zimmer.
 *  - Zimmer-Vorschaubild + Name
 *  - 6 Standard-Punkte + beliebig viele "Sonstiges"-Punkte (mit Autocomplete)
 *  - Alles erledigt → große Erfolgs-Karte + Button "Neue Reinigung vorbereiten"
 */
export default function RoomChecklist({ room, onRoomChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extraText, setExtraText]     = useState("");
  const [suggestions, setSug]         = useState([]);
  const [showSug, setShowSug]         = useState(false);
  const [highlight, setHighlight]     = useState(-1);
  const [deadlineDraft, setDeadlineDraft] = useState("");
  const [showDeadlinePicker, setShowDeadlinePicker] = useState(false);
  const inputRef = useRef(null);
  const boxRef   = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/rooms/${room.id}/checklist`);
      setData({ ...data, custom: data.custom || [] });
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [room.id]);

  // Autocomplete für Zusatzpunkte
  useEffect(() => {
    const q = extraText.trim();
    if (q.length < 1) { setSug([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/checklist/suggest", { params: { q, limit: 6 } });
        setSug(data.suggestions || []);
        setHighlight(-1);
      } catch { /* schweigend */ }
    }, 120);
    return () => clearTimeout(t);
  }, [extraText]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const persist = async (nextItems, nextCustom, nextDeadline) => {
    setBusy(true);
    try {
      const payload = { items: nextItems, custom: nextCustom };
      // undefined → Backend behält bestehenden Wert nicht; null → leeren
      if (nextDeadline !== undefined) payload.deadline = nextDeadline;
      else if (data?.deadline)         payload.deadline = data.deadline;
      const { data: resp } = await api.put(`/rooms/${room.id}/checklist`, payload);
      setData({ ...resp, custom: resp.custom || [] });
      if (resp.auto_status === "frei") {
        toast.success(`${room.name} — alles erledigt · Zimmer fertig vorbereitet`);
        onRoomChange?.();
      }
    } catch (e) {
      toast.error("Checkliste konnte nicht gespeichert werden");
    } finally { setBusy(false); }
  };

  const toggle       = (key) => data && persist({ ...data.items, [key]: !data.items[key] }, data.custom);
  const toggleCustom = (id)  => data && persist(data.items, data.custom.map(c => c.id === id ? { ...c, done: !c.done } : c));
  const removeCustom = (id)  => data && persist(data.items, data.custom.filter(c => c.id !== id));

  const markAllDone = async () => {
    if (!data) return;
    const allItems  = Object.fromEntries(Object.keys(data.labels).map(k => [k, true]));
    const allCustom = data.custom.map(c => ({ ...c, done: true }));
    await persist(allItems, allCustom);
  };

  const addCustom = (raw) => {
    const label = (raw ?? extraText).trim();
    if (!label) return;
    if (data.custom.some(c => c.label.toLowerCase() === label.toLowerCase())) {
      toast.info("Dieser Punkt steht schon auf der Liste"); return;
    }
    const id = `x-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const nextCustom = [...data.custom, { id, label, done: false }];
    persist(data.items, nextCustom);
    setExtraText(""); setSug([]); setShowSug(false);
    inputRef.current?.focus();
  };

  // Klick auf „Neue Reinigung vorbereiten" öffnet Deadline-Picker
  const openReset = () => {
    setDeadlineDraft(defaultDeadline());
    setShowDeadlinePicker(true);
  };
  const cancelReset = () => { setShowDeadlinePicker(false); setDeadlineDraft(""); };
  const confirmReset = async () => {
    if (!data) return;
    if (!deadlineDraft) { toast.error("Bitte Datum & Uhrzeit wählen"); return; }
    const zero = Object.fromEntries(Object.keys(data.labels).map(k => [k, false]));
    // Sonstiges-Punkte werden bei „Neue Reinigung vorbereiten" komplett gelöscht
    await persist(zero, [], deadlineDraft);
    setShowDeadlinePicker(false);
    setDeadlineDraft("");
  };

  const onKey = (e) => {
    if (showSug && suggestions.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setHighlight(h => Math.max(h - 1, -1)); return; }
      if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); addCustom(suggestions[highlight]); return; }
      if (e.key === "Escape") { setShowSug(false); return; }
    }
    if (e.key === "Enter") { e.preventDefault(); addCustom(); }
  };

  if (!data) return null;
  const stdTotal   = Object.keys(data.labels).length;
  const stdDone    = Object.values(data.items).filter(Boolean).length;
  const custTotal  = data.custom.length;
  const custDone   = data.custom.filter(c => c.done).length;
  const total      = stdTotal + custTotal;
  const done       = stdDone + custDone;
  const allDone    = done === total && total > 0;

  return (
    <div className="msb-card overflow-hidden p-0" data-testid={`checklist-${room.id}`}>
      {/* Kopfbereich mit Zimmer-Foto */}
      <div className="flex items-center gap-3 p-3 border-b border-border">
        <img src={roomImage(room.name)} alt={room.name}
             className="w-20 h-16 object-cover rounded-lg border border-border flex-shrink-0"/>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold truncate" style={{fontFamily:"'Work Sans',sans-serif"}}>{room.name}</h3>
          <p className="text-[11px] text-muted-foreground">Reinigungs-Checkliste</p>
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${allDone ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
          {done}/{total}
        </span>
        {!allDone && total > 0 && (
          <button
            type="button"
            onClick={markAllDone}
            disabled={busy}
            title="Alles auf einen Schlag abhaken (Zimmer als fertig markieren)"
            data-testid={`checklist-all-done-${room.id}`}
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors whitespace-nowrap flex items-center gap-1"
          >
            <CheckCircle size={12} weight="fill"/> Alle
          </button>
        )}
      </div>

      {allDone ? (
        // Erledigt-Ansicht
        <div className="p-6 text-center" data-testid={`checklist-done-${room.id}`}>
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center shadow-lg mb-3">
            <CheckCircle size={38} weight="fill" color="white"/>
          </div>
          <div className="text-lg font-bold text-emerald-800" style={{fontFamily:"'Work Sans',sans-serif"}}>
            Zimmer fertig vorbereitet
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {data.updated_at && new Date(data.updated_at).toLocaleString("de-DE",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}
            {data.updated_by_name ? ` · ${data.updated_by_name}` : ""}
          </p>
          {!showDeadlinePicker ? (
            <Button onClick={openReset} disabled={busy}
                    className="mt-4 rounded-xl bg-primary"
                    data-testid={`checklist-reset-${room.id}`}>
              <Sparkle size={14} weight="bold"/>
              <span className="ml-1.5">Neue Reinigung vorbereiten</span>
            </Button>
          ) : (
            <div className="mt-4 pt-4 border-t border-border text-left" data-testid={`checklist-deadline-picker-${room.id}`}>
              <label className="block text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                Erledigen bis
              </label>
              <input
                type="datetime-local"
                value={deadlineDraft}
                onChange={(e)=>setDeadlineDraft(e.target.value)}
                className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid={`checklist-deadline-input-${room.id}`}
              />
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={cancelReset} disabled={busy}
                        variant="outline" className="rounded-lg flex-1"
                        data-testid={`checklist-reset-cancel-${room.id}`}>
                  Abbrechen
                </Button>
                <Button size="sm" onClick={confirmReset} disabled={busy || !deadlineDraft}
                        className="rounded-lg bg-primary flex-1"
                        data-testid={`checklist-reset-confirm-${room.id}`}>
                  <Sparkle size={12} weight="bold"/>
                  <span className="ml-1">Starten</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Aktive Checkliste
        <div className="p-4">
          {/* Standard-Punkte – abgehakte verschwinden sofort */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            {Object.entries(data.labels)
              .filter(([key]) => !data.items[key])
              .map(([key, label]) => (
                <label key={key}
                       className="flex items-center gap-2 text-sm select-none cursor-pointer"
                       data-testid={`checklist-item-${room.id}-${key}`}>
                  <Checkbox checked={false} disabled={busy}
                            onCheckedChange={()=>toggle(key)}/>
                  <span className="text-foreground">{label}</span>
                </label>
              ))}
          </div>

          {/* Custom-Punkte – abgehakte verschwinden sofort */}
          {data.custom.some(c => !c.done) && (
            <div className="mt-4 pt-3 border-t border-dashed border-border">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Sonstige Aufgaben</div>
              <ul className="space-y-1">
                {data.custom.filter(c => !c.done).map(c => (
                  <li key={c.id}
                      className="group flex items-center gap-2 text-sm"
                      data-testid={`checklist-custom-${room.id}-${c.id}`}>
                    <Checkbox checked={false} disabled={busy} onCheckedChange={()=>toggleCustom(c.id)}/>
                    <span className="flex-1">{c.label}</span>
                    <button type="button" onClick={()=>removeCustom(c.id)}
                            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600 transition p-1"
                            title="Entfernen">
                      <X size={12} weight="bold"/>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Custom-Eingabe */}
          <div className="mt-3 pt-3 border-t border-border relative" ref={boxRef}>
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={extraText}
                onChange={(e)=>{ setExtraText(e.target.value); setShowSug(true); }}
                onFocus={()=>setShowSug(true)}
                onKeyDown={onKey}
                placeholder="Sonstiges hinzufügen …"
                className="rounded-lg h-9 text-sm flex-1"
                data-testid={`checklist-custom-input-${room.id}`}
                autoComplete="off"/>
              <Button size="sm" onClick={()=>addCustom()} className="rounded-lg bg-primary h-9"
                      data-testid={`checklist-custom-add-${room.id}`}>
                <Plus size={14} weight="bold"/>
              </Button>
            </div>
            {showSug && suggestions.length > 0 && (
              <ul className="absolute z-30 mt-1 left-0 right-12 max-h-52 overflow-auto rounded-xl border border-border bg-white shadow-lg py-1">
                {suggestions.map((s, i) => (
                  <li key={s}>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); addCustom(s); }}
                      onMouseEnter={() => setHighlight(i)}
                      className={`w-full text-left px-3 py-1.5 text-sm transition ${i === highlight ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"}`}>
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-[11px] text-muted-foreground mt-3">
            {data.deadline && (
              <div className={`mb-1 font-semibold ${isOverdue(data.deadline) ? "text-red-700" : "text-foreground"}`}
                   data-testid={`checklist-deadline-${room.id}`}>
                <Clock size={12} weight={isOverdue(data.deadline) ? "fill" : "duotone"} className="inline mr-1"/>
                Erledigen bis: {fmtDeadline(data.deadline)}
                {isOverdue(data.deadline) && <span className="ml-1">· überfällig</span>}
              </div>
            )}
            {data.updated_at ? (
              <>Zuletzt: {new Date(data.updated_at).toLocaleString("de-DE",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                {data.updated_by_name ? ` · ${data.updated_by_name}` : ""}</>
            ) : (<>Noch nicht bearbeitet</>)}
          </div>
        </div>
      )}
    </div>
  );
}
